import Markdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkMath from "remark-math"
import { type JSX, useEffect, useRef, useState } from "react"
import { loadBundle } from "../lazy"
import { type FileEntry, fetchFiles } from "./api"
import { shorten } from "./text"

// shared/literature/ — the papers agents have downloaded while working the
// objective. None of this is in our database: the sandbox's filesystem is the
// only record, so this reads it live and says so plainly when the runtime
// cannot be reached.
//
// A paper arrives as two files: the PDF, and a .txt extraction whose front
// matter carries what we know about it — title, authors, a summary, and where
// it came from. The listing already parses that header, so a paper is one row
// built from the pair.

export const LITERATURE_PATH = "shared/literature"

type Paper = {
  // Whichever file the metadata came from — the extraction if there is one.
  key: string
  title: string
  authors?: string
  summary?: string
  landingUrl?: string
  // Our own copy, which is what gets embedded. Absent for an extraction with
  // no PDF beside it.
  documentPath?: string
  extractionPath?: string
  fileName: string
  sourceLabel?: string
  venue?: string
  published?: string
}

export function Literature({
  filesUrl,
  fileUrl,
  openPath,
  onOpenPaper,
}: {
  filesUrl: string
  fileUrl: string
  // Which paper is open, if any. Held by the app rather than here so the rail
  // can close it by navigating to Literature with nothing open.
  openPath?: string
  onOpenPaper: (path: string | undefined) => void
}): JSX.Element {
  const [entries, setEntries] = useState<FileEntry[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    void fetchFiles(filesUrl, LITERATURE_PATH).then((listing) => {
      if (cancelled) return
      if (listing) setEntries(listing.entries)
      else setFailed(true)
    })

    return () => {
      cancelled = true
    }
  }, [filesUrl])

  const papers = collate(entries ?? [])
  const open = papers.find((paper) => paper.key === openPath)

  if (open) {
    return <Paper paper={open} fileUrl={fileUrl} onBack={() => onOpenPaper(undefined)} />
  }

  return (
    <article className="workspace__doc workspace__doc--wide">
      <header className="workspace__doc-head">
        <h1 className="workspace__doc-title">Literature</h1>
        <p className="workspace__doc-meta">
          {failed
            ? "Could not reach this workspace's sandbox."
            : entries === null
              ? "Loading…"
              : papers.length === 0
                ? "Nothing yet. Agents put what they read here."
                : `${papers.length} ${papers.length === 1 ? "paper" : "papers"} agents have reviewed.`}
        </p>
      </header>

      {papers.length > 0 && (
        <div className="workspace__table-shell">
          <table className="workspace__table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Authors</th>
                <th scope="col">Venue</th>
                <th scope="col">Published</th>
                <th scope="col">Summary</th>
              </tr>
            </thead>
            <tbody>
              {papers.map((paper) => (
                <tr key={paper.key} onClick={() => onOpenPaper(paper.key)}>
                  <td>
                    <button type="button" className="workspace__table-open">
                      {paper.title}
                    </button>
                  </td>
                  <td className="workspace__table-authors">{paper.authors ?? "—"}</td>
                  <td className="workspace__table-venue">{paper.venue || "—"}</td>
                  <td className="workspace__table-published">{paper.published || "—"}</td>
                  <td className="workspace__table-summary">
                    {paper.summary ? shorten(firstParagraph(paper.summary)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  )
}

// One paper: the document itself is the page, and what we know about it sits
// beside it. The PDF comes from our own copy rather than the original — no
// framing restrictions, no CORS, and it is the version the agents read.
function Paper({
  paper,
  fileUrl,
  onBack,
}: {
  paper: Paper
  fileUrl: string
  onBack: () => void
}): JSX.Element {
  const src = paper.documentPath ? `${fileUrl}?path=${encodeURIComponent(paper.documentPath)}` : null

  // The same reader the site shows papers with (paper_reader.ts): pdf.js
  // pages, a pager, zoom, and a thumbnail drawer that starts closed. Handed a
  // bare element, it builds its own chrome — so this pane and papers#show get
  // one viewer rather than two that drift.
  const viewer = useRef<HTMLElement>(null)

  // pdf.js arrives on demand — opening a paper is the first moment the
  // workspace needs it, and most sessions never open one. The viewer can
  // therefore be gone by the time the module lands (a reader who clicked back),
  // which is what `cancelled` is for.
  useEffect(() => {
    if (!viewer.current || !src) return

    let cancelled = false
    let mounted: { destroy: () => void } | null = null

    void loadBundle<{ mountPdfViewer: (element: HTMLElement) => { destroy: () => void } }>("pdf-reader")
      .then((module) => {
        if (cancelled || !viewer.current) return
        mounted = module.mountPdfViewer(viewer.current)
      })
      .catch(() => {
        if (!cancelled && viewer.current) {
          viewer.current.textContent = "Could not load the PDF reader. The paper is still downloadable below."
        }
      })

    return () => {
      cancelled = true
      mounted?.destroy()
    }
  }, [src])

  return (
    <article className="paper-detail workspace__paper">
      <main
        className="paper-doc"
        ref={viewer}
        // Keyed on the document so switching papers builds a fresh viewer
        // rather than leaving the last one's pages on screen.
        key={src ?? "none"}
        data-pdf-viewer
        data-pdf-url={src ?? ""}
        data-pdf-title={paper.title}
        data-pdf-download={src ?? ""}
        data-pdf-download-name={paper.fileName}
      />

      <aside className="paper-meta workspace__paper-side">
        <div className="paper-meta__scroll">
          <div className="paper-meta__card">
            <button type="button" className="workspace__paper-back" onClick={onBack}>
              ← Literature
            </button>
            <h1 className="paper-meta__title">{paper.title}</h1>
            {paper.authors && <p className="paper-meta__authors">{paper.authors}</p>}

            {paper.summary && (
              <section className="paper-meta__section">
                <h2 className="paper-meta__section-title">Summary</h2>
                {/* The cataloguer is told inline LaTeX is welcome, so a summary
                    of a mathematics paper is full of it. Rendered the same way
                    the objective and the findings are — $…$ and $$…$$ through
                    KaTeX. Markdown makes the paragraphs, so the blank lines in
                    the front matter's block scalar come out as breaks. */}
                <div className="paper-meta__abstract paper-meta__abstract--rich">
                  <Markdown remarkPlugins={[ remarkMath ]} rehypePlugins={[ rehypeKatex ]}>
                    {paper.summary}
                  </Markdown>
                </div>
              </section>
            )}

            {(paper.landingUrl || src) && (
              <section className="paper-meta__section">
                <h2 className="paper-meta__section-title">Source</h2>
                {paper.sourceLabel && <p className="paper-meta__abstract">{paper.sourceLabel}</p>}
                {paper.landingUrl && (
                  <p className="paper-meta__abstract">
                    <a href={paper.landingUrl} target="_blank" rel="noreferrer noopener">
                      Read more about it ↗
                    </a>
                  </p>
                )}
              </section>
            )}

            {(paper.venue || paper.published) && (
              <section className="paper-meta__section">
                <h2 className="paper-meta__section-title">Published</h2>
                {paper.venue && <p className="paper-meta__abstract">{paper.venue}</p>}
                {paper.published && <p className="paper-meta__abstract">{paper.published}</p>}
              </section>
            )}

            <section className="paper-meta__section">
              <h2 className="paper-meta__section-title">File Details</h2>
              <p className="paper-meta__abstract">{paper.fileName}</p>
            </section>
          </div>
        </div>
      </aside>
    </article>
  )
}

// Fold `1507.00569.pdf.txt` into `1507.00569.pdf`: one paper, not two rows.
// The extraction is where the metadata lives and the PDF is what gets shown,
// so a paper wants both. Either may be missing — a .txt somebody dropped in
// alone is still a paper, and a PDF whose extraction failed is too.
function collate(entries: FileEntry[]): Paper[] {
  const files = entries.filter((entry) => entry.kind === "file" && !isWebPage(entry))
  const extractions = new Map<string, FileEntry>()

  for (const entry of files) {
    if (entry.name.endsWith(".txt")) {
      const source = entry.path.slice(0, -".txt".length)
      if (files.some((candidate) => candidate.path === source)) extractions.set(source, entry)
    }
  }

  const claimed = new Set([ ...extractions.values() ].map((entry) => entry.path))

  return files
    .filter((entry) => !claimed.has(entry.path))
    .map((entry) => {
      const extraction = extractions.get(entry.path)
      const meta = extraction?.front_matter ?? entry.front_matter ?? {}
      return {
        key: entry.path,
        title: extraction?.title || entry.title || entry.name,
        authors: meta.authors,
        summary: meta.summary,
        landingUrl: meta.landing_url || meta.embed_url,
        documentPath: entry.path,
        extractionPath: extraction?.path,
        fileName: entry.name,
        sourceLabel: sourceLabel(meta),
        venue: meta.venue,
        published: meta.published_at,
      }
    })
}

// "arXiv 1507.00569", or just the source when it has no id worth showing.
function sourceLabel(meta: Record<string, string>): string | undefined {
  if (!meta.source) return undefined
  const name = meta.source === "arxiv" ? "arXiv" : meta.source
  return meta.source_id ? `${name} ${meta.source_id}` : name
}

// fetch_url saves whatever it is pointed at into shared/literature, so a page
// an agent read on its way to a paper lands here too. It is not a paper: no
// extraction, nothing to open in the reader, and listing it puts a stray
// `ratio.html` among the citations. The file stays on disk for grep and
// read_file — this is about what the shelf shows.
function isWebPage(entry: FileEntry): boolean {
  return /\.(html?|htm)$/i.test(entry.name)
}

function firstParagraph(summary: string): string {
  return summary.split(/\n{2,}/)[0]
}
